import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Cpu, ShieldCheck, Sparkles } from "lucide-react";
import { BrandLogo, BrandMonogram } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminRole } from "@/lib/utils";

// Skyline SVG lazy-loaded — animated draw-on effect, one-time render.
const SkylineSVG = lazy(() =>
  import("@/components/SkylineSVG").then((m) => ({ default: m.SkylineSVG }))
);

const PILLARS = [
  {
    icon: ShieldCheck,
    title: "Mandatory Gatekeep",
    body: "PRC-verified brokers only. Every member is licensed and vetted before joining the network.",
  },
  {
    icon: Cpu,
    title: "AI Matching Engine",
    body: "Instantly connects buyer requirements to exact inventory — semantic, not just keyword.",
  },
  {
    icon: Sparkles,
    title: "Exclusive ATS Protection",
    body: "Dispute resolution safeguards exclusive listings, keeping your Authority to Sell yours.",
  },
];

export default function LandingPage() {
  const { user, status } = useAuth();
  const isAuthed = status === "authenticated" && !!user;
  const primaryCta = isAuthed
    ? { to: isAdminRole(user.role) ? "/control-panel-x9" : "/dashboard", label: "Go to dashboard" }
    : { to: "/register", label: "Apply for Access" };

  return (
    <div className="mb-page min-h-screen">
      {/* -------------------- HEADER --------------------
          Mobile-safe flex: brand is `flex-shrink-0` and the wordmark
          hides <sm to prevent the 320-430px overlap with the CTA row
          (which used to wrap "Log in" over the wordmark). "Log in" +
          "Apply for Access" both `whitespace-nowrap` so neither can
          break onto a second line. */}
      <header className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-5 py-5 sm:px-6 sm:py-6">
        <Link
          to="/"
          className="mb-focus-ring inline-flex flex-shrink-0 items-center rounded-lg"
          data-testid="landing-header-logo"
        >
          <BrandLogo
            className="h-9"
            wordmarkClassName="hidden sm:inline"
          />
        </Link>
        <nav className="flex flex-shrink-0 items-center gap-3 sm:gap-6">
          {!isAuthed && (
            <Link
              to="/login"
              className="mb-transition whitespace-nowrap text-sm font-medium text-gray-600 hover:text-gray-900"
              data-testid="landing-header-login"
            >
              Log in
            </Link>
          )}
          <Link to={primaryCta.to} data-testid="landing-header-cta">
            <Button className="mb-focus-ring h-10 whitespace-nowrap rounded-xl bg-[#1B2B4B] px-4 text-sm font-semibold text-white hover:bg-[#14213A] sm:px-5">
              {primaryCta.label}
            </Button>
          </Link>
        </nav>
      </header>

      {/* -------------------- HERO --------------------
          Tighter mobile rhythm: reduced top-padding on narrow viewports,
          headline sizes step up cleanly, CTA row stacks full-width under
          400px and wraps side-by-side above. The duplicate
          AnimatedLogoHero lockup that used to render below the CTAs was
          removed (2026-09-03) — the navbar brand is sufficient; only
          the decorative skyline SVG remains. */}
      <section className="mx-auto max-w-6xl px-5 pt-10 pb-16 sm:px-6 sm:pt-20 sm:pb-24">
        <div className="mb-rise max-w-4xl">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-brand-navy/15 bg-brand-navy-soft px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-brand-navy sm:mb-6 sm:text-[11px] sm:tracking-[0.18em]">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-green" />
            PRC-verified · Metro Manila
          </div>
          <h1
            className="text-[2rem] font-bold leading-[1.1] tracking-tight text-gray-900 sm:text-6xl lg:text-7xl"
            data-testid="landing-hero-headline"
          >
            The Exclusive AI-Powered Network for Verified PH Brokers.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-gray-500 sm:mt-8 sm:text-xl">
            A simple and efficient workspace for Metro Manila&apos;s licensed
            brokers — where every listing has a real owner and every match
            starts with intent.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:mt-12 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
            <Link to={primaryCta.to} data-testid="landing-hero-primary" className="w-full sm:w-auto">
              <Button className="mb-focus-ring group h-12 w-full whitespace-nowrap rounded-2xl bg-[#1B2B4B] px-7 text-base font-semibold text-white hover:bg-[#14213A] sm:w-auto">
                {primaryCta.label}
                <ArrowRight className="ml-2 h-4 w-4 mb-transition group-hover:translate-x-0.5" />
              </Button>
            </Link>
            {!isAuthed && (
              <Link
                to="/login"
                className="mb-focus-ring mb-transition inline-flex h-12 w-full items-center justify-center whitespace-nowrap rounded-2xl px-4 text-base font-semibold text-gray-700 hover:text-gray-900 sm:w-auto sm:justify-start"
                data-testid="landing-hero-secondary"
              >
                Log in
              </Link>
            )}
          </div>
        </div>

        {/* Decorative skyline horizon — no logo lockup here anymore.
            Kept behind the aria-hidden guard so screen readers ignore it. */}
        <div
          className="relative mt-10 w-full sm:mt-16"
          data-testid="landing-hero-skyline"
          aria-hidden="true"
        >
          <Suspense fallback={<div className="h-32 sm:h-48" />}>
            <SkylineSVG className="h-32 w-full sm:h-48 lg:h-56" />
          </Suspense>
        </div>
      </section>

      {/* -------------------- VALUE PILLARS -------------------- */}
      <section className="border-t border-gray-100 bg-white/60">
        <div className="mx-auto max-w-6xl px-6 py-20 sm:py-28">
          <div className="mb-16 max-w-2xl">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
              Why MatchBroker
            </div>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">
              Built for how PH brokers actually work.
            </h2>
          </div>
          <div
            className="grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-10"
            data-testid="landing-pillars"
          >
            {PILLARS.map((p, idx) => (
              <article
                key={p.title}
                className="mb-transition group flex flex-col rounded-2xl border border-gray-100 bg-white p-8 shadow-mb-card hover:shadow-mb-card-hover animate-mb-fade-in"
                style={{ animationDelay: `${idx * 80}ms` }}
                data-testid={`landing-pillar-${idx + 1}`}
              >
                <span
                  className={`mb-6 inline-flex h-11 w-11 items-center justify-center rounded-xl ${
                    idx === 0
                      ? "bg-brand-green/10 text-brand-green"
                      : "bg-brand-navy/10 text-brand-navy"
                  }`}
                >
                  <p.icon className="h-5 w-5" />
                </span>
                <h3 className="text-lg font-semibold text-gray-900">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-gray-500">
                  {p.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------- FOOTER -------------------- */}
      <footer className="border-t border-gray-100">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-4 px-6 py-10 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2.5">
            <BrandMonogram className="h-6" />
            <span
              className="text-sm font-bold tracking-tight leading-none"
              data-testid="landing-footer-wordmark"
            >
              <span className="text-brand-green">Match</span>
              <span className="text-brand-navy">Broker</span>
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-gray-500">
            <Link
              to="/privacy"
              className="mb-transition hover:text-gray-900"
              data-testid="landing-footer-privacy"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="mb-transition hover:text-gray-900"
              data-testid="landing-footer-terms"
            >
              Terms of Service
            </Link>
            <span className="text-gray-400">
              © {new Date().getFullYear()} MatchBroker
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
